import { isNumber, uid, unique } from 'radash';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';

import {
    Diagram,
    DiagramConnection,
    VoltageType,
    Zone,
    ZoneValidationError,
    ZoneValidationErrorType,
    ZoneValidator,
} from 'models';

/**
 * Multiplier to normalize a connection's voltage to line-to-line (AC) or full-bus (DC).
 * Single-phase AC → ×√3, pole-to-midpoint DC → ×2, otherwise ×1.
 */
const getVoltageNormalizationMultiplier = (voltageType: VoltageType | null, connection: DiagramConnection): number => {
    const isSinglePhase =
        voltageType === 'AC' && connection.lines.AC.L1 && !connection.lines.AC.L2 && !connection.lines.AC.L3;

    const isPoleToMidpoint =
        voltageType === 'DC' &&
        ((connection.lines.DC['L+'] && connection.lines.DC['M'] && !connection.lines.DC['L-']) ||
            (connection.lines.DC['L-'] && connection.lines.DC['M'] && !connection.lines.DC['L+']));

    if (voltageType === 'AC') return isSinglePhase ? Math.sqrt(3) : 1;
    if (voltageType === 'DC') return isPoleToMidpoint ? 2 : 1;
    return 1;
};

const getVoltageType = (diagram: Diagram, connection: DiagramConnection) => {
    const { from, to } = connection;
    if (!isNumber(from.port) || !isNumber(to.port)) return null;
    const fromCI = diagram.componentInstances[from.componentInstanceId];
    const toCI = diagram.componentInstances[to.componentInstanceId];
    const fromVT = fromCI?.configuration.ports?.[from.port]?.voltageType;
    const toVT = toCI?.configuration.ports?.[to.port]?.voltageType;
    return fromVT === toVT ? fromVT : null;
};

const checkVoltageLevels: ZoneValidator['validator'] = ({ diagram, zones }) => {
    const errors: ZoneValidationError[] = [];

    zones.forEach((zone) => checkVoltageLevelsInZone(errors, diagram, zone));

    return errors;
};

const checkVoltageLevelsInZone = (errors: ZoneValidationError[], diagram: Diagram, zone: Zone) => {
    const toComponentInstances = (ids: string[]) => ids.map((id) => diagram.componentInstances[id]).filter(Boolean);

    const { min, max, minComponentInstances, maxComponentInstances } = getMinAndMaxVoltageForZone(diagram, zone);

    if (min > max) {
        errors.push({
            id: uid(6),
            type: ZoneValidationErrorType.VOLTAGE_LEVELS_MISMATCH,
            level: 'warning',
            payload: {
                zone,
                componentInstances: zone.componentInstances,
                voltage: {
                    min,
                    max,
                    minComponentInstances: toComponentInstances(minComponentInstances),
                    maxComponentInstances: toComponentInstances(maxComponentInstances),
                },
            },
        });

        // A nominal voltage above the maximum is implied by the mismatch, so warning about it too would be noise
        return;
    }

    const { nom, nomComponentInstances } = getHighestNominalVoltageForZone(diagram, zone);

    if (nom > max) {
        errors.push({
            id: uid(6),
            type: ZoneValidationErrorType.VOLTAGE_NOMINAL_EXCEEDS_MAXIMUM,
            level: 'warning',
            payload: {
                zone,
                componentInstances: toComponentInstances(unique([...nomComponentInstances, ...maxComponentInstances])),
                voltage: {
                    nom,
                    max,
                    nomComponentInstances: toComponentInstances(nomComponentInstances),
                    maxComponentInstances: toComponentInstances(maxComponentInstances),
                },
            },
        });
    }
};

const getMinAndMaxVoltageForZone = (diagram: Diagram, zone: Zone) => {
    const minCandidates = getVoltageCandidatesForZone(diagram, zone, 'min');
    const maxCandidates = getVoltageCandidatesForZone(diagram, zone, 'max');

    const min = Math.max(...minCandidates.map((c) => c.value), -Infinity);
    const max = Math.min(...maxCandidates.map((c) => c.value), Infinity);

    return {
        min,
        max,
        minComponentInstances: getComponentInstanceIdsAt(minCandidates, min),
        maxComponentInstances: getComponentInstanceIdsAt(maxCandidates, max),
    };
};

const getHighestNominalVoltageForZone = (diagram: Diagram, zone: Zone) => {
    const nomCandidates = getVoltageCandidatesForZone(diagram, zone, 'nom');

    const nom = Math.max(...nomCandidates.map((c) => c.value), -Infinity);

    return { nom, nomComponentInstances: getComponentInstanceIdsAt(nomCandidates, nom) };
};

// real max, not Math.min(...maxes)
const getMaxVoltageForZone = (diagram: Diagram, zone: Zone) => {
    const maxCandidates = getVoltageCandidatesForZone(diagram, zone, 'max');

    const max = Math.max(...maxCandidates.map((c) => c.value), -Infinity);

    return { max, maxComponentInstances: getComponentInstanceIdsAt(maxCandidates, max) };
};

type VoltageCandidate = { value: number; componentInstanceId: string };

const getVoltageCandidatesForZone = (
    diagram: Diagram,
    zone: Zone,
    level: 'min' | 'nom' | 'max',
): VoltageCandidate[] => {
    const candidates: VoltageCandidate[] = [];

    zone.connections.forEach((connection) => {
        const voltageType = getVoltageType(diagram, connection);
        const multiplier = getVoltageNormalizationMultiplier(voltageType, connection);

        [connection.from, connection.to].forEach((endpoint) => {
            const voltage = getEndpointVoltage(diagram, endpoint)?.[level];

            if (isNumber(voltage)) {
                candidates.push({ value: multiplier * voltage, componentInstanceId: endpoint.componentInstanceId });
            }
        });
    });

    return candidates;
};

const getComponentInstanceIdsAt = (candidates: VoltageCandidate[], value: number) =>
    unique(
        candidates.filter((c) => c.value === value).map((c) => c.componentInstanceId),
        (id) => id,
    );

const getEndpointVoltage = (diagram: Diagram, endpoint: DiagramConnection['from' | 'to']) => {
    const { componentInstanceId: id, port: portIndex } = endpoint;

    if (portIndex === null) return;

    const componentInstance = diagram.componentInstances[id];

    // Series/parallel counts applied, so the array voltage is compared rather than a single module's
    return ComponentInstanceHelpers.normalise(componentInstance, portIndex)?.voltage;
};

export { checkVoltageLevels, getMinAndMaxVoltageForZone, getMaxVoltageForZone, getVoltageNormalizationMultiplier };
